import { useState } from 'react';

import { Button } from '@/components/common/Button';
import { TemplateTaskForm } from '@/components/templates/TemplateTaskForm';
import { getCategoryDisplayName } from '@/utils/category-utils';
import { formatTimingDescription } from '@/utils/template-display-utils';
import type { TemplateTask } from '@/types/checklist-template';

interface TemplateTaskEditorProps {
  tasks: TemplateTask[];
  onChange: (tasks: TemplateTask[]) => void;
}

function reindex(tasks: TemplateTask[]): TemplateTask[] {
  return tasks.map((t, i) => ({ ...t, sortOrder: i }));
}

export function TemplateTaskEditor({ tasks, onChange }: TemplateTaskEditorProps) {
  const [editingId, setEditingId] = useState<string | null>(null);

  function moveUp(index: number) {
    if (index === 0) return;
    const next = [...tasks];
    // Swap positions
    const temp = next[index - 1]!;
    next[index - 1] = next[index]!;
    next[index] = temp;
    onChange(reindex(next));
  }

  function moveDown(index: number) {
    if (index === tasks.length - 1) return;
    const next = [...tasks];
    // Swap positions
    const temp = next[index + 1]!;
    next[index + 1] = next[index]!;
    next[index] = temp;
    onChange(reindex(next));
  }

  function handleDelete(id: string) {
    if (!window.confirm('Delete task?')) return;
    onChange(reindex(tasks.filter((t) => t.id !== id)));
    if (editingId === id) setEditingId(null);
  }

  function handleSave(saved: TemplateTask) {
    if (editingId === 'new') {
      onChange(reindex([...tasks, saved]));
    } else {
      onChange(reindex(tasks.map((t) => (t.id === saved.id ? saved : t))));
    }
    setEditingId(null);
  }

  return (
    <div className="flex flex-col gap-2">
      {tasks.map((task, index) => {
        if (editingId === task.id) {
          return (
            <TemplateTaskForm
              key={task.id}
              task={task}
              sortOrder={task.sortOrder}
              availableDependencies={tasks.filter((t) => t.sortOrder < task.sortOrder)}
              onSave={handleSave}
              onCancel={() => setEditingId(null)}
            />
          );
        }

        return (
          <div
            key={task.id}
            data-testid="task-editor-row"
            className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg px-4 py-3 flex items-start gap-3"
          >
            {/* Reorder controls */}
            <div className="flex flex-col gap-0.5 flex-shrink-0 pt-0.5">
              <button
                type="button"
                onClick={() => moveUp(index)}
                disabled={index === 0}
                aria-label="Move task up"
                className="p-0.5 text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="18 15 12 9 6 15" />
                </svg>
              </button>
              <button
                type="button"
                onClick={() => moveDown(index)}
                disabled={index === tasks.length - 1}
                aria-label="Move task down"
                className="p-0.5 text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
            </div>

            {/* Task info */}
            <div className="flex-1 min-w-0">
              <p className="text-body-sm font-medium text-[var(--color-text-primary)] truncate">
                {task.title}
              </p>
              <p className="text-body-xs text-[var(--color-text-muted)]">
                {formatTimingDescription(task)}
              </p>
            </div>

            {/* Category badge */}
            <span className="flex-shrink-0 text-body-xs text-[var(--color-text-secondary)] bg-[var(--color-elevated)] rounded px-1.5 py-0.5">
              {getCategoryDisplayName(task.category)}
            </span>

            {/* Row actions */}
            <div className="flex gap-1 flex-shrink-0">
              <Button variant="ghost" onClick={() => setEditingId(task.id)} className="px-2 py-1 text-body-xs">
                Edit
              </Button>
              <Button
                variant="danger"
                onClick={() => handleDelete(task.id)}
                className="px-2 py-1 text-body-xs"
              >
                Delete
              </Button>
            </div>
          </div>
        );
      })}

      {/* Add new task form inline */}
      {editingId === 'new' && (
        <TemplateTaskForm
          sortOrder={tasks.length}
          availableDependencies={tasks}
          onSave={handleSave}
          onCancel={() => setEditingId(null)}
        />
      )}

      {/* Add Task button — only when not already adding */}
      {editingId !== 'new' && (
        <button
          type="button"
          data-testid="add-task-btn"
          onClick={() => setEditingId('new')}
          className="mt-1 flex items-center gap-2 px-4 py-2.5 rounded-md text-body-sm font-medium text-[var(--color-accent)] border border-dashed border-[var(--color-accent)]/40 hover:border-[var(--color-accent)] hover:bg-[var(--color-accent-subtle)] transition-colors"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          Add Task
        </button>
      )}
    </div>
  );
}
